import { Order, Pagination, PaginationResult, SearchKey, SearchQueryResult, WithFiles, SearchQuery as SQ } from '@vierkant-software/types__api';
import { AppService } from 'src/services/app.service';
import { DateTime } from 'luxon';
import { Observable, Subject, debounceTime, merge } from 'rxjs';
import { SearchQuery } from './person-search.util';
import { inject } from '@angular/core';

export class PersonSearchService {
  private static DEBOUNCE_TIME = 250;
  static DEFAULT_FIELDS: Partial<Record<SearchKey, Order>> = {
    firstname: "ASC",
    lastname:  "ASC",
  };
  static DEFAULT_PAGINATION: Pagination = {
    page: 0,
    size: 25,
  };

  private app: AppService = inject(AppService);

  private searchSubject: Subject<SearchQuery> = new Subject<SearchQuery>();
  private searchNoDebounceSubject: Subject<SearchQuery> = new Subject<SearchQuery>();
  public searchSubscription: Observable<WithFiles<PaginationResult<SearchQueryResult>> | undefined> = merge(
    this.searchSubject.pipe(debounceTime(PersonSearchService.DEBOUNCE_TIME), this._searchObservable()),
    this.searchNoDebounceSubject.pipe(this._searchObservable(),)
  );

  public search(query: SearchQuery){
    this.searchSubject.next(query);
  }

  public searchNoDebounce(query: SearchQuery) {
    this.searchNoDebounceSubject.next(query);
  }
  private _searchObservable(){
    const internalSearch = (x: SearchQuery) => this._searchInternal(x);
    return (source: Observable<SearchQuery>): Observable<WithFiles<PaginationResult<SearchQueryResult>> | undefined> =>
      new Observable(subscriber => {
        const subscription = source.subscribe({
          next(value) {
            internalSearch(value)
              .then((result) => subscriber.next(result))
              .catch((error) => {
                error.displayErrorToast?.("Fehler bei der Personensuche");
                console.error(error);
                subscriber.next(undefined);
              });
          },
          error(error) {
            subscriber.error(error);
            subscription.unsubscribe();
          },
          complete() {
            subscriber.complete();
            subscription.unsubscribe();
          }
        });
        return () => subscription.unsubscribe();
      });
  }

  private async _searchInternal(value: SearchQuery) {
    const parseSearchValue = (x: string) => this.parseSearchValue(x);
    if (value === undefined || value === null) return;
      const terms = parseSearchValue(value.term);
      const fields = {...PersonSearchService.DEFAULT_FIELDS, ...value.fields};
      const pagination = value.pagination ?? PersonSearchService.DEFAULT_PAGINATION;
      const withFiles = value.withFiles ?? false;
      const query = {
        terms,
        fields,
        withFiles,
        employee:      value.employee,
        customer:      value.customer,
        member:        value.member,
        workhoursType: value.workhoursType,
        visibilityFor: value.visibilityFor,
        departments:   value.departments,
      } as SQ;

      return this.app.api.UserWorker.search(query, pagination);
  }

  // explode terms into their data types
  private parseSearchValue(value: string): {string: string[], number: number[], dateTime: DateTime[]} {
    const locale = this.app.locale;
    return (value ?? '').split(" ").reduce((curr, val) => {
      const number = ["", null, undefined].includes(val) ? NaN : Number(val);
      const date = DateTime.fromFormat(val, DateTime.parseFormatForOpts(DateTime.DATE_SHORT, {locale}));
      if (!isNaN(number))
        curr.number.push(number);
      else if (date.isValid)
        curr.dateTime.push(date);
      else
        curr.string.push(val);
      return curr;
    }, ({
      string:   [],
      number:   [],
      dateTime: [],
    }));
  }
}
